import { FeedbacksTable } from '@/components/feedbacks/feedbacks-table';
import { InfoPage } from '@/components/info-page';
import { TableFilterInputField } from '@/components/table/table-filter-field';
import { TableFilterPlainDatePicker } from '@/components/table/table-filter-plain-date-picker';
import { TableFilterResetButton } from '@/components/table/table-filter-reset-button';
import { DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { EmptyArray, getSessionFeedbacks } from '@/lib/api-calls';
import { useRowsBulkSelection } from '@/lib/bulk-selection';
import { usePaginatedFilters } from '@/lib/filtering';
import { paginationRequestSchema } from '@/lib/pagination';
import { BulkUpdateLateFeedback } from '@/routes/_layout/sessions/-components/bulk-update-late-feedback';
import { BulkUpdateStudentAttendance } from '@/routes/_layout/sessions/-components/bulk-update-student-attended';
import { FrequentTimeZones } from '@airreading/common';
import {
  GetSessionFeedbacksModifier,
  SessionFeedbackWithAssessment,
  toSessionFeedbackCompoundId,
} from '@airreading/common/data';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useMemo } from 'react';
import { Temporal } from 'temporal-polyfill';
import { z } from 'zod';
import { studentTabs } from '../../-components/tabs';

const PAST_STUDENT_FEEDBACKS = 'past-student-feedbacks';

function pastStudentFeedbacksMenuItemTestId(action: string) {
  return `${PAST_STUDENT_FEEDBACKS}-menu-item-${action}`;
}

export const Route = createFileRoute('/_layout/students/$studentId/past-student-feedbacks/')({
  component: PastStudentFeedbacksIndex,
  validateSearch: paginationRequestSchema.extend({
    query: z.string().optional(),
    sessionPlainDateInUSCentral: z.string().optional(),
  }),
});

function PastStudentFeedbacksIndex() {
  const queryClient = useQueryClient();
  const { studentId } = Route.useParams();
  const {
    pageIndex,
    pageSize,
    onPaginationChange,
    filters: {
      query: [[query, activeQuery], setQuery],
      sessionPlainDateInUSCentral: [
        [sessionPlainDateInUSCentral, activeSessionPlainDateInUSCentral],
        setSessionPlainDateInUSCentral,
      ],
    },
    resetFilters,
  } = usePaginatedFilters(Route.id, ['query', 'sessionPlainDateInUSCentral']);

  const requestQuery = {
    pageIndex,
    pageSize,
    filters: {
      query: activeQuery,
      studentId: studentId,
      startOnOrBeforePlainDateInUSCentral: activeSessionPlainDateInUSCentral
        ? activeSessionPlainDateInUSCentral
        : Temporal.Now.plainDateTimeISO(FrequentTimeZones.UsCentral).toPlainDate().toString(),
    },
  } satisfies GetSessionFeedbacksModifier;

  const {
    data: sessionFeedbacksResp,
    isPending: isLoadingSessionFeedbacks,
    error,
  } = useQuery(getSessionFeedbacks(requestQuery));

  const sessionFeedbacks = useMemo(() => sessionFeedbacksResp?.sessionFeedbacks || EmptyArray, [sessionFeedbacksResp]);

  const { rowSelection, rowDataSelection, onRowSelectionChange, getRowId, resetAllRowSelection } =
    useRowsBulkSelection<SessionFeedbackWithAssessment>({
      getRowId: (row) => toSessionFeedbackCompoundId(row),
      rowIdToData: (id) => sessionFeedbacks.find((feedback) => toSessionFeedbackCompoundId(feedback) === id),
    });

  const filterLeftComponents = useMemo(
    () => (
      <>
        <TableFilterInputField
          className="w-[250px] lg:w-[350px] xl:w-[400px] 2xl:w-[450px]"
          placeholder="Search feedbacks by teacher, email or lesson name"
          value={query ?? ''}
          data-testid={`${PAST_STUDENT_FEEDBACKS}-filter-input-search`}
          onChange={(event) => {
            setQuery(event.target.value);
          }}
        />
        <TableFilterPlainDatePicker
          onPlainDateSelect={setSessionPlainDateInUSCentral}
          selectedPlainDateString={sessionPlainDateInUSCentral}
          onClear={() => setSessionPlainDateInUSCentral(undefined)}
          tableFilterDatePickerProps={{
            placeholder: 'Filter by session date (CT)',
            'data-testid': `${PAST_STUDENT_FEEDBACKS}-filter-date-session`,
          }}
        />
        <TableFilterResetButton
          show
          onClick={resetFilters}
          data-testid={`${PAST_STUDENT_FEEDBACKS}-filter-button-reset`}
        />
      </>
    ),
    [query, setSessionPlainDateInUSCentral, sessionPlainDateInUSCentral, queryClient, resetFilters, setQuery],
  );

  if (error) {
    return (
      <InfoPage headerText="Past Student Feedback" tabs={studentTabs({ studentId })}>
        <div>Failed to fetch past student feedback entries. Please try again or notify Air Reading.</div>
      </InfoPage>
    );
  }

  return (
    <InfoPage
      headerText="Past Student Feedback"
      data-testid={PAST_STUDENT_FEEDBACKS}
      tabs={studentTabs({ studentId })}
      breadcrumbs={{
        linkItems: [
          {
            label: 'Students',
            link: {
              to: '/students',
            },
          },
        ],
        currentPage: 'Past Student Feedback',
      }}
    >
      <FeedbacksTable
        emptyState="No past student feedback found"
        sessionFeedbacks={sessionFeedbacks}
        isLoading={isLoadingSessionFeedbacks}
        from="student"
        tablePrefix={PAST_STUDENT_FEEDBACKS}
        bulkActionsTestId={`${PAST_STUDENT_FEEDBACKS}-table-button-bulk-actions`}
        tableHookProps={{
          state: {
            rowSelection,
            pagination: {
              pageIndex,
              pageSize,
            },
          },
          onPaginationChange,
          onRowSelectionChange,
          getRowId,
          rowCount: sessionFeedbacksResp?.total || 0,
        }}
        filters={{
          filterLeftComponents,
        }}
        bulkActionsMenuItems={
          <>
            <BulkUpdateStudentAttendance
              key={'cancel-attendance'}
              selectedFeedbacks={rowDataSelection}
              trigger={
                <DropdownMenuItem
                  onSelect={(e) => e.preventDefault()}
                  data-testid={pastStudentFeedbacksMenuItemTestId('cancel-attendance')}
                >
                  Cancel student attendance
                </DropdownMenuItem>
              }
              attended={false}
              onSuccess={resetAllRowSelection}
            />
            <BulkUpdateStudentAttendance
              key={'mark-attendance'}
              selectedFeedbacks={rowDataSelection}
              trigger={
                <DropdownMenuItem
                  onSelect={(e) => e.preventDefault()}
                  data-testid={pastStudentFeedbacksMenuItemTestId('mark-attendance')}
                >
                  Mark student as attended
                </DropdownMenuItem>
              }
              attended={true}
              onSuccess={resetAllRowSelection}
            />
            <BulkUpdateLateFeedback
              lateFeedbackDismissed={true}
              trigger={
                <DropdownMenuItem
                  onSelect={(e) => e.preventDefault()}
                  data-testid={pastStudentFeedbacksMenuItemTestId('dismiss-late-feedback')}
                >
                  Dismiss late feedback tag
                </DropdownMenuItem>
              }
              selectedFeedbacks={rowDataSelection}
              onSuccess={resetAllRowSelection}
            />
          </>
        }
      />
    </InfoPage>
  );
}
